import { Capacitor } from '@capacitor/core'
import mixpanel from 'mixpanel-browser'

import { environment } from './config/environment'
import { tenantSlugs } from './modules/healthblocks/ServerProvider'
import { sha256 } from './utils/sha256'

import type { Dict } from 'mixpanel-browser'
import type { TenantSlug } from './modules/healthblocks/ServerProvider'

const testIDs = new Set(['7'])

class Mixpanel {
  static _instance: Mixpanel

  private enabled: boolean = false
  private defaultProperties: Record<string, string> = {}

  constructor(token: string, debug?: boolean) {
    if (Mixpanel._instance) {
      return Mixpanel._instance
    }

    mixpanel.init(token, { debug, api_host: 'https://api-eu.mixpanel.com' })

    Mixpanel._instance = this
  }

  public reset(): void {
    mixpanel.reset()
  }

  // Enable
  // ------------------------------------------------------------------------- /
  public disable(): void {
    this.enabled = false
  }

  public enable(): void {
    this.enabled = true
  }

  // Profile properties
  // ------------------------------------------------------------------------- /
  public disableIfTestUser(id?: string): void {
    if (id && testIDs.has(id)) {
      console.info('Mixpanel: Disabling tracking for test user')
      this.disable()
    } else {
      this.enable()
    }
  }

  public registerProperties(values: Record<string, unknown>): void {
    if (environment.env !== 'production') {
      // console.info('Mixpanel: registering properties', values)
    }
    mixpanel.register(values)
  }

  public async identify(userId: string): Promise<void> {
    try {
      // 1. Encode the userId string to an ArrayBuffer
      const encoder = new TextEncoder()
      const data = encoder.encode(userId)

      // 2. Hash the ArrayBuffer using SHA-256
      const hashBytes = await sha256(data)

      // 3. Convert the hashBytes to a hex string
      const hashArray = [...hashBytes]
      const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('')

      mixpanel.identify(hashHex)
    } catch (error) {
      console.error('Mixpanel: identify error', error)
    }
  }

  public setProfileProperties(values: Record<string, unknown>): void {
    mixpanel.people.set(values)
  }

  public setDefaults(values: Record<string, string>): void {
    this.defaultProperties = values
  }

  // Tracking
  // ------------------------------------------------------------------------- /
  public track(eventName: string, properties?: Dict): void {
    if (environment.env !== 'production') {
      console.info('Mixpanel: ' + eventName, properties, this.defaultProperties)
    }
    if (this.enabled) {
      const tenant = location.pathname.split('/')[1] as TenantSlug
      mixpanel.track(eventName, {
        time: Date.now(),
        tenant: tenantSlugs.includes(tenant) ? tenant : undefined,
        // mixpanel-browser defaults $current_url to the full href. Query
        // strings can carry secrets (the OAuth callback lands on /login with
        // the authorization code), so only ever report origin + path
        $current_url: location.origin + location.pathname,
        ...this.defaultProperties,
        ...properties,
      })
    }
  }
}

// Initialise Mixpanel
const mixpanelInstance = new Mixpanel(
  environment.mixpanel.token,
  // environment.env !== 'production'
)

mixpanelInstance.setDefaults({
  environment: environment.env,
  platform: Capacitor.getPlatform(),
  app_version: APP_VERSION,
})

export { Mixpanel, mixpanelInstance as mixpanel }
